// Copyright 2016 Sorama B.V.

import { Disposable } from "aurelia-binding";
import { HttpResponseMessage, RequestMessage } from "aurelia-http-client";
import { PromiseReason } from "datatypes/PromiseExtension";
import { HttpResponseMessageError } from "errors/HttpResponseMessageError";
import { ProgressIndicator } from "libraries/ProgressIndicator/ProgressIndicator";

export enum WebRequestPriority {
    /**
     * Indicates that this is a high priority request which should be sent as soon as possible.
     */
    HighPriority,

    /**
     * Indicates that this is a default priority request and should be sent as soon as there is a slot available which is and there are no high priority requests pending.
     */
    NormalPriority,

    /**
     * Indicates that this request is low priority (typically a long duration compute call).
     * Request of this kind get a maximum number of parallel requests but are scheduled with the same priority as normal requests.
     */
    LowPriority,
}

export interface IWebRequestInterceptorHandler {
    /**
     * Called before a request is sent
     */
    onSendRequest(request: RequestMessage): Promise<void>;

    /**
     * Called before a request is sent
     */
    onReceiveResponse(request: HttpResponseMessage): Promise<void>;

    /**
     * Called when a response errors, return true to retry the operation, false to propagate error
     */
    onResponseError(error: HttpResponseMessage): Promise<boolean>;
}

export interface IPagedRequest<T> {
    /**
     * Contains all items retrieved so far from the request.
     */
    readonly total: T[];

    /**
     * Specifies if all items have been retrieved.
     */
    readonly finished: boolean;

    /**
     * Reads the next items and updates the request properties.
     */
    next(): Promise<T[]>;

    /**
     * Reads the complete request until there are no more entities to fetch
     */
    readToEnd(): Promise<T[]>;

    /**
     * Gets the total number of elements that match the query
     */
    getCount(): Promise<number>;
}

export interface IPageResult<T> {
    Items: T[];
    NextPageLink: string;
    Count: number;
}

export interface IWebRequest<T> {
    /**
     * Url for the request
     */
    readonly url: string;

    /**
     * Add a download progress callback to this request (cannot be used in combination with 'withProgres')
     * @param callback Callback function to call when a download progress update is available
     */
    withDownloadProgress(callback: (progressIndicator: ProgressIndicator) => void): IWebRequest<T>;

    /**
     * Add an upload progress callback to this request
     * @param callback Callback function to call when a upload progress update is available
     */
    withUploadProgress(callback: (progressIndicator: ProgressIndicator) => void): IWebRequest<T>;

    /**
     * Add a computational progress callback to this call (requires support from server side, and cannot be used with 'withDownloadProgress')
     * @param callback Callback function to call when a computational status update is available
     */
    withProgress(callback: (progress: number) => void): IWebRequest<T>;

    /**
     * Set the priority for this request
     * @param priority Priority for this request, see WebRequestPriority for more information
     */
    withPriority(priority: WebRequestPriority): IWebRequest<T>;

    /**
     * Add a state object to this request which is return as a member of the response message
     * @param state State object to return in the response message
     */
    withState(state: unknown): IWebRequest<T>;

    /**
     * Indicates that the browser may cache this request
     */
    withCache(): IWebRequest<T>;

    /**
     * Indicates that the body of this request is a value type (eg. number) and should be treated as such
     */
    withValue(): IWebRequest<T>;

    /**
     * Indicates that the reponse type of this request will be a binary response, the resulting content of the response will be a Blob
     */
    withBinaryResponse(): IWebRequest<Blob>;

    /**
     * Indicates that the content type of this request is binary (blob)
     */
    withBinaryContent(): IWebRequest<T>;

    /**
     * Indicates that the request should result in a BSON response, use this response type for large data structures
     */
    withBSONResponse(): IWebRequest<T>;

    /*
     * Updates request url
     */
    withUrl(url: string): IWebRequest<T>;

    /**
     * Indicates that the request could be aborted
     * @param signal AbortSignal to use for aborting the request
     */
    withAbortSignal(signal: AbortSignal): IWebRequest<T>;

    /**
     * Determines if the current request setup can be used with progress reporting
     */
    supportsProgressWithCurrentSettings(): boolean;

    /**
     * Sends the request
     */
    send(): PromiseReason<TypedHttpResponseMessage<T>, HttpResponseMessageError>;
}

export class TypedHttpResponseMessage<T> extends HttpResponseMessage {
    public content: T;
    public state: unknown;
}

export abstract class IWebRequestHandler {
    /**
     * Adds an intercepter for all requests to a specific domain
     * @param domain Domain for which to intercept calls (ie. local.sorama.eu)
     * @param handler Interceptor handler
     */
    abstract addInterceptor(domain: string, handler: IWebRequestInterceptorHandler): Disposable;

    /**
     * Create a new HTTP GET request with the provided URL
     * @param url Url for the request
     */
    abstract createGetRequest<T>(url: string): IWebRequest<T>;

    /**
     * Create a new HTTP HEAD request with the provided URL
     * @param url Url for the request
     */
    abstract createHeadRequest<T>(url: string): IWebRequest<T>;

    /**
     * Create a new HTTP POST request with the provided URL and optionally a body
     * @param url Url for the request
     * @param body Optional body to send with the request
     */
    abstract createPostRequest<T>(url: string, body?: unknown): IWebRequest<T>;

    /**
     * Create a new HTTP PUT request with the provided URL and optionally a body
     * @param url Url for the request
     * @param body Optional body to send with the request
     */
    abstract createPutRequest<T>(url: string, body?: unknown): IWebRequest<T>;

    /**
     * Create a new HTTP DELETE request with the provided URL and optionally a body
     * @param url Url for the request
     * @param body Optional body to send with the request
     */
    abstract createDeleteRequest<T>(url: string, body?: unknown): IWebRequest<T>;
}
